/**
 * Renders a native <select> alongside every button-style variant-picker fieldset,
 * then uses CSS to show only the select on mobile and only the buttons on desktop.
 * The select never owns the form state itself, selecting an option just clicks
 * the matching radio input, so all of the theme's existing variant-picker JS
 * (price updates, availability, connected product URLs for combined listings)
 * keeps working unmodified.
 *
 * Also adds a small "Can't decide? Surprise me" link under any option with 4+
 * values that is not a size/weight style option (flavours, mostly). It picks a
 * random in-stock value by selecting the matching radio.
 */
(() => {
  if (window.__rdMobileVariantSelectApplied) return;
  window.__rdMobileVariantSelectApplied = true;

  const UNAVAILABLE_SUFFIX = ' - Unavailable';
  const SKIP_NAME = /size|weight|pack|qty|quantity|amount|volume|length|width|kg/i;

  const injectStyles = () => {
    if (document.getElementById('rd-surprise-styles')) return;
    const css = document.createElement('style');
    css.id = 'rd-surprise-styles';
    css.textContent =
      '.rd-surprise{display:inline-flex;align-items:center;gap:7px;margin:2px 0 10px;padding:0;border:0;background:none;color:#686D67;font:inherit;font-size:13px;font-weight:600;cursor:pointer;text-align:left}' +
      '.rd-surprise strong{color:#B9684B;font-weight:800;text-decoration:underline;text-underline-offset:3px}' +
      '.rd-surprise:hover strong{color:#9f5039}' +
      '.rd-surprise svg{width:16px;height:16px;flex:0 0 auto;fill:none;stroke:#B9684B;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}' +
      '.rd-surprise:focus-visible{outline:2px solid #B9684B;outline-offset:3px;border-radius:4px}';
    document.head.appendChild(css);
  };

  const addSurprise = (fieldset, radios, anchor) => {
    const name = (fieldset.querySelector('legend')?.childNodes[0]?.textContent || '').trim();
    if (radios.length < 4 || SKIP_NAME.test(name)) return;
    if (fieldset.dataset.rdSurprise === 'true') return;
    fieldset.dataset.rdSurprise = 'true';
    injectStyles();

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'rd-surprise';
    button.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="3.5" width="17" height="17" rx="3.5"/><circle cx="8.5" cy="8.5" r="1" fill="#B9684B"/><circle cx="15.5" cy="8.5" r="1" fill="#B9684B"/><circle cx="12" cy="12" r="1" fill="#B9684B"/><circle cx="8.5" cy="15.5" r="1" fill="#B9684B"/><circle cx="15.5" cy="15.5" r="1" fill="#B9684B"/></svg>' +
      '<span>Can’t decide? <strong>Surprise me</strong></span>';

    button.addEventListener('click', () => {
      const pool = radios.filter(
        (r) => !r.hasAttribute('aria-disabled') && r.dataset.optionAvailable !== 'false' && !r.checked
      );
      if (!pool.length) return;
      const pick = pool[Math.floor(Math.random() * pool.length)];
      pick.checked = true;
      pick.dispatchEvent(new Event('change', { bubbles: true }));
      pick.dispatchEvent(new Event('input', { bubbles: true }));
      const label = button.querySelector('span');
      if (label) label.innerHTML = 'Picked <strong>' + pick.value.replace(/[&<>]/g, '') + '</strong>. Tap to reroll';
    });

    anchor.insertAdjacentElement('afterend', button);
  };

  const buildSelectForFieldset = (fieldset) => {
    if (fieldset.dataset.rdMobileSelectBuilt === 'true') return;
    fieldset.dataset.rdMobileSelectBuilt = 'true';

    // Clear helpers left behind by a previous (morphed-away) copy of this fieldset
    let stale = fieldset.nextElementSibling;
    while (stale && (stale.classList.contains('rd-mobile-variant-select') || stale.classList.contains('rd-surprise'))) {
      const next = stale.nextElementSibling;
      stale.remove();
      stale = next;
    }

    const legend = fieldset.querySelector('legend');
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    if (radios.length === 0) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'rd-mobile-variant-select';

    if (legend) {
      const label = document.createElement('label');
      label.className = 'rd-mobile-variant-select__label';
      label.textContent = legend.childNodes[0] ? legend.childNodes[0].textContent.trim() : '';
      wrapper.appendChild(label);
    }

    const selectWrap = document.createElement('div');
    selectWrap.className = 'rd-mobile-variant-select__control';

    const select = document.createElement('select');
    select.className = 'rd-mobile-variant-select__input';
    select.setAttribute('aria-label', legend ? legend.textContent.trim() : 'Variant option');

    radios.forEach((radio) => {
      const option = document.createElement('option');
      option.value = radio.value;
      const isAvailable = radio.dataset.optionAvailable !== 'false';
      option.textContent = isAvailable ? radio.value : radio.value + UNAVAILABLE_SUFFIX;
      option.disabled = radio.hasAttribute('aria-disabled');
      option.selected = radio.checked;
      select.appendChild(option);
    });

    select.addEventListener('change', () => {
      const match = radios.find((radio) => radio.value === select.value);
      if (match && !match.checked) {
        match.checked = true;
        match.dispatchEvent(new Event('change', { bubbles: true }));
        match.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    // Keep the select in sync if the radio state changes from elsewhere
    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked && select.value !== radio.value) {
          select.value = radio.value;
        }
      });
    });

    const caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    caret.setAttribute('viewBox', '0 0 10 6');
    caret.setAttribute('aria-hidden', 'true');
    caret.classList.add('rd-mobile-variant-select__caret');
    caret.innerHTML = '<path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>';

    selectWrap.appendChild(select);
    selectWrap.appendChild(caret);
    wrapper.appendChild(selectWrap);

    fieldset.insertAdjacentElement('afterend', wrapper);
    addSurprise(fieldset, radios, wrapper);
  };

  const initVariantPicker = (root) => {
    const fieldsets = (root || document).querySelectorAll(
      '.variant-option--buttons:not(.variant-option--swatches)'
    );
    fieldsets.forEach(buildSelectForFieldset);
  };

  document.addEventListener('DOMContentLoaded', () => initVariantPicker(document));
  if (document.readyState !== 'loading') initVariantPicker(document);

  // Rebuild inside re-rendered sections (theme editor + AJAX cart/variant swaps)
  document.addEventListener('shopify:section:load', (event) => initVariantPicker(event.target));

  // Variant swaps morph the picker markup, so rebuild anything that lost its helpers
  let timer;
  new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(() => initVariantPicker(document), 80);
  }).observe(document.body, { childList: true, subtree: true });
})();